// Invite links: https://my.passcall.io/?invite=<code>
//
// Every account owns one code, kept in Users/{uid}.inviteCode and published as
// Invites/{code} = { uid, name, createdAt } (anyone holding the code may read that
// one document; the collection cannot be listed). A friend who opens the link gets
// the code parked in localStorage, signs up or in, and the two accounts become
// mutual friends at once:
//   1. the invitee adds the inviter to their own Users.friends and Friendships.approved
//      (ordinary owner writes);
//   2. the invitee records Invites/{code}/accepted/{inviteeUid};
//   3. that record is what lets the invitee append their own uid, and nothing else,
//      to the inviter's Users.friends and Friendships.approved (see firestore.rules).
// Making a new code ("New link") retires the old one: the rules compare against the
// code currently on the inviter's Users document.
import { writable } from "svelte/store";

export const APP_URL = "https://my.passcall.io";
const STORAGE_KEY = "passcall.invite";
const CODE_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789"; // no 0/o/1/l/i
export const CODE_LENGTH = 12;

/** Name of the person whose link brought the visitor here (for the sign-in page). */
export const pendingInviter = writable(null);

export function inviteLink(code) {
  return `${APP_URL}/?invite=${encodeURIComponent(code)}`;
}

export function isInviteCode(code) {
  return typeof code === "string" && new RegExp(`^[${CODE_ALPHABET}]{${CODE_LENGTH}}$`).test(code);
}

function newCode() {
  const bytes = new Uint8Array(CODE_LENGTH);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join("");
}

function store(code) {
  try { if (code) window.localStorage.setItem(STORAGE_KEY, code); else window.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* unavailable */ }
}

/** The code parked by an earlier visit, or null. */
export function pendingInvite() {
  try {
    const code = window.localStorage.getItem(STORAGE_KEY);
    return isInviteCode(code) ? code : null;
  } catch (e) {
    return null;
  }
}

export function clearPendingInvite() {
  store(null);
}

/**
 * Take ?invite=<code> off the address (sign-up and the email links reload the page,
 * so it is parked in localStorage) and return the pending code, if any.
 */
export function inviteFromUrl() {
  let code = null;
  try {
    const url = new URL(window.location.href);
    code = url.searchParams.get("invite");
    if (code !== null) {
      url.searchParams.delete("invite");
      window.history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
    }
  } catch (e) { /* no URL API */ }
  if (isInviteCode(code)) store(code);
  return pendingInvite();
}

/** { uid, name } for a code, or null when the code is unknown. */
export async function fetchInvite(db, code) {
  if (!isInviteCode(code)) return null;
  const doc = await db.collection("Invites").doc(code).get();
  if (!doc.exists) return null;
  const d = doc.data() || {};
  return typeof d.uid === "string" && d.uid ? { uid: d.uid, name: typeof d.name === "string" ? d.name : "" } : null;
}

/**
 * The signed-in account's invite code, created on first use. `fresh` retires the
 * current code and issues another. Also makes sure the account's friend lists exist,
 * since the rules only let an invitee append to lists that are already there.
 */
export async function ensureInviteCode(db, firebase, user, name, { fresh = false } = {}) {
  if (!user || !user.uid) throw new Error("Not signed in.");
  const users = db.collection("Users").doc(user.uid);
  const snap = await users.get();
  const data = snap.exists ? snap.data() || {} : {};
  let code = isInviteCode(data.inviteCode) ? data.inviteCode : null;
  const update = {};
  if (!Array.isArray(data.friends)) update.friends = [];
  if (!code || fresh) {
    code = newCode();
    await db.collection("Invites").doc(code).set({
      uid: user.uid,
      name: String(name || data.name || user.displayName || "").trim().slice(0, 80),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
    update.inviteCode = code;
  }
  if (Object.keys(update).length) await users.set(update, { merge: true });
  const friendships = db.collection("Friendships").doc(user.uid);
  const f = await friendships.get();
  if (!f.exists) await friendships.set({ approved: [], paused: [] });
  return code;
}

/** Text for the share sheet, email or text message. */
export function inviteMessage(code, fromName) {
  const who = fromName ? `${fromName} here. ` : "";
  return `${who}I'd like you in my PassCall window. It's a window between our homes: when we both walk past our screens at the same time, we see each other. No calls to make, and it's free.\n\n` +
    `Open this link to set up your account, and we'll be on each other's friends list straight away:\n${inviteLink(code)}`;
}

/**
 * Join the signed-in account to the inviter behind `code`. Returns { uid, name } of the
 * inviter, or null if the code is unknown or the account's own. Clears the parked code
 * either way (a dead link should not be retried on every sign-in).
 */
export async function acceptInvite(db, firebase, user, code) {
  clearPendingInvite();
  if (!user || !user.uid) return null;
  const invite = await fetchInvite(db, code);
  if (!invite || invite.uid === user.uid) return null;
  const union = firebase.firestore.FieldValue.arrayUnion;
  const me = user.uid;
  // 1. My side.
  await db.collection("Users").doc(me).set({ friends: union(invite.uid) }, { merge: true });
  await db.collection("Friendships").doc(me).set({ approved: union(invite.uid) }, { merge: true });
  // 2. The record that authorises step 3.
  await db.collection("Invites").doc(code).collection("accepted").doc(me)
    .set({ at: firebase.firestore.FieldValue.serverTimestamp() });
  // 3. Their side: append my uid only.
  await db.collection("Users").doc(invite.uid).update({ friends: union(me) });
  await db.collection("Friendships").doc(invite.uid).update({ approved: union(me) });
  return invite;
}
